import { cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPortal,
  SheetTitle,
} from "./sheet";
import {
  Sidebar as BaseSidebar,
  SidebarContent as BaseSidebarContent,
  SidebarGroup as BaseSidebarGroup,
  SidebarGroupContent as BaseSidebarGroupContent,
  SidebarHeader as BaseSidebarHeader,
  SidebarInset as BaseSidebarInset,
  SidebarMenu as BaseSidebarMenu,
  SidebarMenuButton as BaseSidebarMenuButton,
  SidebarMenuItem as BaseSidebarMenuItem,
  SidebarProvider as BaseSidebarProvider,
  SidebarRail as BaseSidebarRail,
  SidebarTrigger as BaseSidebarTrigger,
  useSidebar,
} from "./sidebar";

// No-animation Sheet components without overlay
const FpSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "left" | "right" | "top" | "bottom";
  }
>(({ className, children, side = "left", ...props }, ref) => (
  <SheetPortal>
    {/* No SheetOverlay here - this removes the dark background */}
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        // Base sheet positioning
        "fixed z-50 gap-4 p-6 shadow-lg",
        // Remove all animations and transitions
        "transition-none animate-none duration-0",
        // Override all animation states
        "data-[state=open]:animate-none data-[state=closed]:animate-none",
        "data-[state=open]:duration-0 data-[state=closed]:duration-0",
        // Position based on side
        side === "left" && "inset-y-0 left-0 h-full border-r",
        side === "right" && "inset-y-0 right-0 h-full border-l",
        side === "top" && "inset-x-0 top-0 border-b",
        side === "bottom" && "inset-x-0 bottom-0 border-t",
        className,
      )}
      {...props}
    >
      {children}
      {/* Remove the close button since we have our own trigger */}
    </SheetPrimitive.Content>
  </SheetPortal>
));
FpSheetContent.displayName = "FpSheetContent";

// Context to override isMobile when forceSheet is used
const ForceSheetContext = React.createContext<boolean>(false);

// Custom hook that overrides isMobile when forceSheet is active
const useFpSidebar = () => {
  const originalContext = useSidebar();
  const forceSheet = React.useContext(ForceSheetContext);

  if (forceSheet) {
    // Create a new toggleSidebar that always uses mobile state
    const toggleSidebar = React.useCallback(() => {
      originalContext.setOpenMobile(!originalContext.openMobile);
    }, [originalContext.setOpenMobile, originalContext.openMobile]);

    // Add keyboard shortcut for forceSheet mode
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "b" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // Override isMobile and toggleSidebar for forceSheet mode
    return {
      ...originalContext,
      isMobile: true,
      toggleSidebar,
    };
  }

  return originalContext;
};

// FP-styled sidebar components with no animations and minimal rounding
const FpSidebarProvider = React.forwardRef<
  React.ElementRef<typeof BaseSidebarProvider>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarProvider> & {
    forceSheet?: boolean;
  }
>(({ className, forceSheet = false, children, ...props }, ref) => {
  return (
    <BaseSidebarProvider ref={ref} className={cn(className)} {...props}>
      <ForceSheetContext.Provider value={forceSheet}>
        {children}
      </ForceSheetContext.Provider>
    </BaseSidebarProvider>
  );
});
FpSidebarProvider.displayName = "FpSidebarProvider";

const fpSidebarVariants = cva("border-r rounded-none", {
  variants: {
    colorScheme: {
      default: "",
      background: "[&>[data-sidebar=sidebar]]:!bg-background",
    },
  },
  defaultVariants: {
    colorScheme: "default",
  },
});

const FpSidebar = React.forwardRef<
  React.ElementRef<typeof BaseSidebar>,
  React.ComponentPropsWithoutRef<typeof BaseSidebar> & {
    colorScheme?: "default" | "background";
    forceSheet?: boolean;
  }
>(
  (
    {
      className,
      children,
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      colorScheme = "default",
      forceSheet = false,
      ...props
    },
    ref,
  ) => {
    const { isMobile, openMobile, setOpenMobile } = useFpSidebar();

    const sidebarStyle =
      colorScheme === "background"
        ? ({
            "--sidebar-background": "var(--background)",
          } as React.CSSProperties)
        : undefined;

    if (isMobile || forceSheet) {
      // Use mobile state for consistency with toggleSidebar behavior
      const sheetOpen = openMobile;
      const setSheetOpen = setOpenMobile;

      return (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <FpSheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className={cn(
              "w-[--sidebar-width] p-0 text-sidebar-foreground [&>button]:hidden",
              // Force dark background for the sheet version
              forceSheet
                ? "bg-popover text-popover-foreground"
                : colorScheme === "background"
                  ? "bg-background"
                  : "bg-sidebar",
            )}
            style={
              {
                "--sidebar-width": "18rem",
                ...sidebarStyle,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              {" "}
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </FpSheetContent>
        </Sheet>
      );
    }

    return (
      <BaseSidebar
        ref={ref}
        className={cn(fpSidebarVariants({ colorScheme }), className)}
        side={side}
        variant={variant}
        collapsible={collapsible}
        style={sidebarStyle}
        {...props}
      >
        {children}
      </BaseSidebar>
    );
  },
);
FpSidebar.displayName = "FpSidebar";

const FpSidebarHeader = React.forwardRef<
  React.ElementRef<typeof BaseSidebarHeader>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarHeader>
>(({ className, ...props }, ref) => (
  <BaseSidebarHeader
    ref={ref}
    className={cn("border-b p-2 rounded-none", className)}
    {...props}
  />
));
FpSidebarHeader.displayName = "FpSidebarHeader";

const FpSidebarContent = React.forwardRef<
  React.ElementRef<typeof BaseSidebarContent>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarContent>
>(({ className, ...props }, ref) => (
  <BaseSidebarContent ref={ref} className={cn(className)} {...props} />
));
FpSidebarContent.displayName = "FpSidebarContent";

const FpSidebarGroup = React.forwardRef<
  React.ElementRef<typeof BaseSidebarGroup>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarGroup>
>(({ className, ...props }, ref) => (
  <BaseSidebarGroup ref={ref} className={cn(className)} {...props} />
));
FpSidebarGroup.displayName = "FpSidebarGroup";

const FpSidebarGroupContent = React.forwardRef<
  React.ElementRef<typeof BaseSidebarGroupContent>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarGroupContent>
>(({ className, ...props }, ref) => (
  <BaseSidebarGroupContent ref={ref} className={cn("", className)} {...props} />
));
FpSidebarGroupContent.displayName = "FpSidebarGroupContent";

const FpSidebarMenu = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenu>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenu>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenu ref={ref} className={cn("gap-1", className)} {...props} />
));
FpSidebarMenu.displayName = "FpSidebarMenu";

const FpSidebarMenuItem = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenuItem>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenuItem>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenuItem ref={ref} className={cn("", className)} {...props} />
));
FpSidebarMenuItem.displayName = "FpSidebarMenuItem";

const FpSidebarMenuButton = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenuButton>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenuButton>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenuButton
    ref={ref}
    className={cn("rounded-none", className)}
    {...props}
  />
));
FpSidebarMenuButton.displayName = "FpSidebarMenuButton";

const FpSidebarInset = React.forwardRef<
  React.ElementRef<typeof BaseSidebarInset>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarInset>
>(({ className, ...props }, ref) => (
  <BaseSidebarInset
    ref={ref}
    className={cn("rounded-none", className)}
    {...props}
  />
));
FpSidebarInset.displayName = "FpSidebarInset";

const FpSidebarRail = React.forwardRef<
  React.ElementRef<typeof BaseSidebarRail>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarRail>
>(({ className, ...props }, ref) => (
  <BaseSidebarRail ref={ref} className={cn("", className)} {...props} />
));
FpSidebarRail.displayName = "FpSidebarRail";

const FpSidebarTrigger = React.forwardRef<
  React.ElementRef<typeof BaseSidebarTrigger>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarTrigger>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useFpSidebar();

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      toggleSidebar();
      onClick?.(event);
    },
    [toggleSidebar, onClick],
  );

  return (
    <BaseSidebarTrigger
      ref={ref}
      className={cn("rounded-md w-7 h-7 flex-shrink-0", className)}
      onClick={handleClick}
      {...props}
    />
  );
});
FpSidebarTrigger.displayName = "FpSidebarTrigger";

export {
  FpSidebar,
  FpSidebarContent,
  FpSidebarGroup,
  FpSidebarGroupContent,
  FpSidebarHeader,
  FpSidebarInset,
  FpSidebarMenu,
  FpSidebarMenuButton,
  FpSidebarMenuItem,
  FpSidebarProvider,
  FpSidebarRail,
  FpSidebarTrigger,
  useSidebar,
};
